import React, { useState } from 'react';
import {
  Layout,
  Typography,
  Dropdown,
  Space,
  Button,
  Modal,
  message,
  Table,
  Tag
} from 'antd';
import {
  LockOutlined,
  UserOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import history from '../utils/history';
import ChangePasswordModal from './ChangePasswordModal';
import axiosInstance from '../utils/axios';

const { Header } = Layout;
const { Title, Text } = Typography;

const CustomNavbar = ({ collapsed, setCollapsed }) => {
  const [mOpen, setMOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [verificationData, setVerificationData] = useState(null);

  const userRole = localStorage.getItem('user_role');

  const handleLogout = () => {
    localStorage.clear();
    history.replace('/');
  };

  const startScanner = () => {
    setScannerOpen(true);

    setTimeout(() => {
      if (typeof window.Html5QrcodeScanner === 'undefined') {
        message.error('QR scanner failed to load. Please refresh the page.');
        setScannerOpen(false);
        return;
      }

      let html5QrcodeScanner;

      try {
        html5QrcodeScanner = new window.Html5QrcodeScanner(
          "qr-reader",
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1,
            supportedScanTypes: [window.Html5QrcodeScanType.SCAN_TYPE_CAMERA]
          },
          false
        );

        html5QrcodeScanner.render(
          async (decodedText) => {
            html5QrcodeScanner.clear();
            document.getElementById('qr-reader').innerHTML = '';
            setScannerOpen(false);

            const tokenMatch = decodedText.match(/\/event-ticket\/([a-z0-9\-]+)/i);
            const ticketToken = tokenMatch ? tokenMatch[1] : null;

            if (!ticketToken) {
              message.error('Invalid QR code');
              return;
            }

            try {
              const role = localStorage.getItem('user_role');
              let ticketData;

              if (role === 'theatre') {
                const response = await axiosInstance.post('booking/ticket/verify/', {
                  ticket_token: ticketToken
                });
                ticketData = response.data;
              } else if (role === 'event') {
                const response = await axiosInstance.post('news_and_events/event-ticket-verify/', {
                  ticket_token: ticketToken
                });

                // ✅ DEBUG ALERT (MOBILE FRIENDLY)
                // alert('✅ API SUCCESS (EVENT)(Please note this is only for testing and in dev instance and you can continue by closing.)\n\n' + JSON.stringify(response.data));

                console.log('EVENT API RESPONSE:', response.data);

                ticketData = response.data;
              } else {
                throw new Error('Scanner not available for this user role');
              }

              setVerificationData(ticketData);
              setVerificationModalVisible(true);
            } catch (error) {
              const role = localStorage.getItem('user_role');
              if (role === 'event') {
                const errData = error.response?.data || {};
                // alert('✅ API Error \n\n' + JSON.stringify(error.response));
                message.error(errData?.message);

                /* const structuredError = {
                  status: errData.status || 'error',
                  message: errData.message || errData.detail || 'Verification failed',
                  verified_count: errData.verified_count || 0,
                  total_tickets: errData.total_tickets || 0,
                  email: errData.email || null,
                  event: errData.event || null,
                  isError: true
                };
                setVerificationData(structuredError);
                setVerificationModalVisible(true); */
              } else {
                const errorMsg =
                  error.message ||
                  error.response?.data?.message ||
                  error.response?.data?.detail ||
                  (error.response?.data && typeof error.response.data === 'object'
                    ? Object.values(error.response.data)[0]
                    : null) ||
                  'Invalid or unknown ticket';
                message.error(errorMsg);
              }
            }
          },
          (error) => {
            console.warn('Scanning...', error);
          }
        );
      } catch (err) {
        console.error("QR Scanner init error:", err);
        message.error('Camera access denied or not supported');
        setScannerOpen(false);
        if (html5QrcodeScanner) html5QrcodeScanner.clear();
      }
    }, 600);
  };

  const items = [
    {
      label: (
        <div onClick={() => setMOpen(true)}>
          <LockOutlined style={{ marginRight: 8 }} />
          Change Password
        </div>
      ),
      key: '0',
    },
    { type: 'divider' },
    {
      label: (
        <Button danger style={{ width: "100%" }} onClick={handleLogout}>
          Logout
        </Button>
      ),
      key: '3',
    },
  ];

  // Smart columns — handles both snapshot and booking formats
  const seatColumns = [
    { title: 'Section', dataIndex: 'section', key: 'section' },
    { title: 'Block', dataIndex: 'block', key: 'block' },
    { title: 'Row', dataIndex: 'row', key: 'row' },
    {
      title: 'Seat Number',
      key: 'seat_number',
      render: (_, record) => {
        // booking uses seat_number (string), snapshot uses number (number)
        return record.seat_number ?? record.number ?? '-';
      }
    },
    {
      title: 'Type',
      key: 'seat_type',
      render: (_, record) => {
        return record.seat_type || '-';
      }
    },
  ];

  return (
    <Header
      className="custom-navbar"
      style={{
        padding: '0 24px',
        background: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0,21,41,.08)',
        position: 'sticky',
        top: 0,
        zIndex: 1050,
      }}
    >
      <Button
        type="text"
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={() => setCollapsed(!collapsed)}
        className="sidebar-toggle"
        style={{ fontSize: '16px', width: 40, height: 40 }}
      />

      <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
        Getam Cine World
      </Title>

      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        {(userRole === 'theatre' || userRole === 'event') && (
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<QrcodeOutlined style={{ fontSize: 22 }} />}
            onClick={startScanner}
            title="Scan Ticket QR Code"
          />
        )}

        <Dropdown menu={{ items }} trigger={['click']}>
          <Space style={{ cursor: 'pointer' }}>
            <UserOutlined style={{ fontSize: '20px', color: '#000' }} />
          </Space>
        </Dropdown>
      </div>

      <ChangePasswordModal open={mOpen} setOpen={setMOpen} />

      <Modal
        title="Scan Ticket QR Code"
        open={scannerOpen}
        onCancel={() => {
          setScannerOpen(false);
          const reader = document.getElementById('qr-reader');
          if (reader) reader.innerHTML = '';
        }}
        footer={null}
        width={440}
        centered
        destroyOnClose
      >
        <div
          id="qr-reader"
          style={{
            width: '100%',
            margin: '0 auto',
            borderRadius: 12,
            overflow: 'hidden',
            background: '#000',
            minHeight: 320,
          }}
        />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary">
            Align the QR code within the frame
          </Text>
        </div>
      </Modal>

      {/* Ticket Verification Modal */}
      <Modal
        title={
          verificationData?.status === 'verified' ? (
            <Space>
              <Tag color="green">VERIFIED</Tag>
              {verificationData.message || 'Ticket Verified Successfully'}
            </Space>
          ) : (
            'Ticket Verification'
          )
        }
        open={verificationModalVisible}
        onOk={() => setVerificationModalVisible(false)}
        onCancel={() => setVerificationModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setVerificationModalVisible(false)}>
            OK
          </Button>
        ]}
        width={700}
      >
        {verificationData && (
          <div>
            {verificationData.sections ? (
              // EVENT FORMAT
              <div>
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 8 }}>Verification Summary</h4>
                  <p><Tag color="blue">Event Tickets</Tag></p>
                  {/* ✅ EMAIL ADDED */}
                  <p>
                    <strong>Email:</strong>{' '}
                    <Tag color="purple">
                      {verificationData.email ? verificationData.email : <Tag color="red">No Email</Tag>}
                    </Tag>
                  </p>

                  {/* ✅ EVENT NAME (optional but useful) */}
                  <p>
                    <strong>Event:</strong>{' '}
                    {verificationData.event || 'N/A'}
                  </p>
                  <p>
                    <strong>Verified:</strong>{' '}
                    {verificationData.verified_count || 0}/{verificationData.total_tickets || 0}
                    <Tag
                      color={verificationData.status === 'verified' ? 'green' : 'orange'}
                      style={{ marginLeft: 8 }}
                    >
                      {verificationData.status === 'verified' ? 'Completed' : 'Pending'}
                    </Tag>
                  </p>
                </div>
                {verificationData.sections && verificationData.sections.length > 0 && (
                  <div>
                    <h4 style={{ margin: '24px 0 12px', fontSize: '16px' }}>Sections</h4>
                    <Table
                      dataSource={verificationData.sections}
                      columns={[
                        { title: 'Section', dataIndex: 'title', key: 'title' },
                        {
                          title: 'Quantity',
                          key: 'quantity',
                          render: (_, record) => (
                            <Tag color="cyan">{record.quantity}</Tag>
                          )
                        }
                      ]}
                      pagination={false}
                      rowKey="title"
                      bordered
                      size="middle"
                    />
                  </div>
                )}
              </div>
            ) : (
              // THEATRE FORMAT
              <div>
                <p><strong>Booking ID:</strong> {verificationData.booking_id || 'N/A'}</p>
                <p><strong>Source:</strong> <Tag color="blue">{(verificationData.source || 'Unknown').toUpperCase()}</Tag></p>

                <h4 style={{ margin: '24px 0 12px', fontSize: '16px' }}>Seats</h4>
                <Table
                  dataSource={verificationData.seats || []}
                  columns={seatColumns}
                  pagination={false}
                  rowKey={(record) => record.id || Math.random()}
                  bordered
                  size="middle"
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Header>
  );
};

export default CustomNavbar;